import React, { useEffect, useRef } from "react";
import { Routes, Route, Navigate, BrowserRouter } from "react-router-dom";
import "./toastStyles.css";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Navbar from "./components/Entry.js";
import LessonCom from "./components/lesson_Dashboard/LessonCom";
import CreateAccount from "./pages/auth/CreateAccount";
import CreateAccountForm from "./pages/CreateAccountForm";
import VerificationPage from "./pages/VerificationPage";
import LessonDashboard from "./components/lesson_Dashboard/LessonDashboard";
import TraceApp from "./components/Foundation/TraceCom";
import FoundationLessonCom from "./components/Foundation/FoundationLessonCom";
import SubscriptionPage from "./pages/payment";
import AfterLogin from "./pages/auth/AfterLogin";
import YearsDashboard from "./components/Years/YearsDashboard";
import SettingsDashboard from "./components/Profile/SettingsDashboard.js";
import ProfileEdit from "./components/Profile/ProfileEdit.js";
import About from "./assets/utils/About.js";
import FAQ from "./assets/utils/FAQ.js";
import ContactUs from "./components/Profile/ContactUs.js";
import YearSelection from "./components/Profile/YearSelection.js";
import ReportSecurityLayer from "./components/Report/Report.jsx";
import AIHandler from "./components/HandleAIQuestions/AIHandler.js";
import { Sounds } from "./assets/asset.js";
import { Provider, useSelector } from "react-redux";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import store from "./store/store.js";
import LoginForm from "./pages/auth/LoginPage";
import Report from "./components/Leaderboard/ReportSection.jsx";
import Badges from "./components/Leaderboard/Badges.jsx";
import LogoutModal from "./components/LogoutModal.jsx";
import LeaderShipBoard from "./components/Leaderboard/LeadershipBoard.jsx";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import DeviceDetector from "./hooks/DeviceDetector.jsx";
import DemoDashboard from "./components/Years/DemoDashboard.js";
import AiParentsReport from "./components/Leaderboard/AiParentsReport.jsx";
import NotFoundPage from "./components/ui/NotFoundPage.jsx";

const queryClient = new QueryClient();

const PublicRoute = ({ element }) => {
  const { token } = useSelector((state) => state.user);
  return !token ? element : <Navigate to="/yearsdashboard" replace />;
};

const IndexRoute = () => {
  const { token } = useSelector((state) => state.user);
  return !token ? <Navbar /> : <Navigate to="/yearsdashboard" replace />;
};

const ProtectedRoute = ({ element }) => {
  const { user, token } = useSelector((state) => state.user);
  return user && token ? element : <Navigate to="/login" replace />;
};

function App() {
  const audioRef = useRef(new Audio(Sounds.backgroundMusic));
  const { showLogoutModal } = useSelector((state) => state.user);
  const { isMuted } = useSelector((state) => state.mute);

  useEffect(() => {
    const audio = audioRef.current;
    audio.loop = true;

    if (!isMuted) {
      audio.play().catch((error) => {
        console.log("Audio playback failed:", error);
      });
    } else {
      audio.pause();
    }

    return () => {
      audio.pause();
      audio.currentTime = 0;
    };
  }, [isMuted]);

  return (
    <div>
      <DeviceDetector />
      {showLogoutModal && <LogoutModal />}
      <Routes>
        <Route index element={<IndexRoute />} />
        <Route
          path="/createaccount"
          element={<PublicRoute element={<CreateAccount />} />}
        />
        <Route
          path="/createaccountform"
          element={<PublicRoute element={<CreateAccountForm />} />}
        />
        <Route
          path="/verification"
          element={<PublicRoute element={<VerificationPage />} />}
        />
        <Route
          path="/login"
          element={<PublicRoute element={<LoginForm />} />}
        />
        <Route
          path="/forgot-password"
          element={<PublicRoute element={<ForgotPasswordPage />} />}
        />
        <Route path="/contact" element={<ContactUs />} />

        {/* Protected Routes */}
        <Route
          path="/afterlogin"
          element={<ProtectedRoute element={<AfterLogin />} />}
        />
        <Route
          path="/yearsdashboard"
          element={<ProtectedRoute element={<YearsDashboard />} />}
        />
        <Route
          path="/lessonCom"
          element={<ProtectedRoute element={<LessonCom />} />}
        />
        <Route
          path="/lessonDashboard"
          element={<ProtectedRoute element={<LessonDashboard />} />}
        />
        <Route
          path="/trace"
          element={<ProtectedRoute element={<TraceApp />} />}
        />
        <Route
          path="/foundationLessonCom"
          element={<ProtectedRoute element={<FoundationLessonCom />} />}
        />
        <Route
          path="/subscription"
          element={<ProtectedRoute element={<SubscriptionPage />} />}
        />
        <Route
          path="/settings"
          element={<ProtectedRoute element={<SettingsDashboard />} />}
        />
        <Route
          path="/editProffile"
          element={<ProtectedRoute element={<ProfileEdit />} />}
        />
        <Route path="/about" element={<ProtectedRoute element={<About />} />} />
        <Route path="/faq" element={<ProtectedRoute element={<FAQ />} />} />

        <Route
          path="/report"
          element={<ProtectedRoute element={<ReportSecurityLayer />} />}
        />
        <Route
          path="/selectYear"
          element={<ProtectedRoute element={<YearSelection />} />}
        />
        <Route path="/AIHandler/:yearId" element={<AIHandler />} />
        <Route
          path="/report/details"
          element={<ProtectedRoute element={<Report />} />}
        />
        <Route
          path="report/ai-report"
          element={<ProtectedRoute element={<AiParentsReport />} />}
        />
        <Route path="/report/badges" element={<Badges />} />
        <Route
          path="/leaderboard"
          element={<ProtectedRoute element={<LeaderShipBoard />} />}
        />
        <Route path="/demoDashboard" element={<DemoDashboard />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </div>
  );
}

const AppWrapper = () => {
  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
               <ToastContainer
            toastClassName="global-toast-bigger font-mali"
            bodyClassName="global-toast-body"
            progressClassName="global-toast-progress"
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
          />
        </BrowserRouter>
      </QueryClientProvider>
    </Provider>
  );
};

export default AppWrapper;
